import React from "react";
import { Button } from "react-bootstrap";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { Lock, LogIn } from "lucide-react";
import AuthenticationService from "../../_services/AuthenticationService";

type Reason = "not-logged-in" | "no-permission";

const UnauthorizedPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const rawReason = params.get("reason");
  const reason: Reason = rawReason === "not-logged-in" ? "not-logged-in" : "no-permission";
  const next = params.get("next") ?? "/";

  const notLoggedIn = reason === "not-logged-in";

  // Self-heal stale "not signed in" URLs: a login redirect recorded before the
  // user signed in (e.g. SessionExpiredModal's return path) can land a
  // now-authenticated user here. If they hold a valid token, send them on to
  // their destination instead of telling a signed-in user they're not signed in.
  if (notLoggedIn) {
    const currentUser = AuthenticationService.currentUserValue;
    if (currentUser) {
      let valid = false;
      try {
        const { exp } = jwtDecode<JwtPayload>((currentUser as any).token);
        valid = typeof exp !== "number" || exp * 1000 > Date.now();
      } catch {
        valid = false;
      }
      if (valid) {
        return <Redirect to={next.startsWith("/") ? next : "/home"} />;
      }
    }
  }

  const heading = notLoggedIn ? "You are not signed in" : "Access Denied";
  const message = notLoggedIn
    ? "This page requires you to be signed in. Sign in to continue."
    : "You don't have permission to view this page. If you believe this is a mistake, contact your administrator to request the appropriate role.";

  const goToLogin = () => {
    history.replace(`/login?next=${encodeURIComponent(next)}`);
  };

  return (
    <div
      style={{
        minHeight: "calc(100vh - 120px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
      }}
    >
      <div
        style={{
          maxWidth: 520,
          width: "100%",
          padding: "2.5rem",
          background: "var(--surface-card)",
          borderRadius: 16,
          boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: notLoggedIn
              ? "linear-gradient(135deg, #dbeafe, #bfdbfe)"
              : "linear-gradient(135deg, #fee2e2, #fecaca)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 24,
          }}
        >
          {notLoggedIn ? (
            <LogIn size={36} color="var(--brand-primary-hover)" />
          ) : (
            <Lock size={36} color="var(--status-danger)" />
          )}
        </div>
        <h2 style={{ margin: "0 0 12px", color: "var(--text-strong)", fontWeight: 600 }}>{heading}</h2>
        <p style={{ margin: "0 0 24px", color: "var(--text-muted)", lineHeight: 1.6 }}>{message}</p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          {notLoggedIn ? (
            <>
              <Button variant="secondary" onClick={() => history.push("/")} style={{ margin: 2 }}>
                Home
              </Button>
              <Button variant="primary" onClick={goToLogin} style={{ margin: 2 }}>
                Sign In
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={() => history.goBack()} style={{ margin: 2 }}>
                Go Back
              </Button>
              <Button variant="primary" onClick={() => history.push("/")} style={{ margin: 2 }}>
                Home
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnauthorizedPage;
